import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { addBankAccount, updateBankAccount, addDebitCard, fetchDriverCerts } from 'actions/profile'
import Page from 'rui/widgets/Page'
import DriveSidebar from 'components/layout/DriveSidebar'
import Panel, { PanelHeading, PanelSubheading, PanelTitle } from 'components/layout/Panel'
import Form from 'components/form'
import Input from 'components/form/Input'
import { minLength, maxLength, exactLength, groupValidations } from 'components/form/validations'
import Step from 'components/misc/Step'
import styled, { css } from 'styled-components/macro'
import { button } from 'styles/mixins/textstyles'
import Icon from 'components/misc/Icon'
import { H6, Caption, Body2 } from 'components/layout/text'
import { overline } from 'styles/mixins/textstyles'
import { Card } from 'components/form/Stripe'
import uniqueId from 'lodash/uniqueId'
import Thin from 'components/layout/Thin'
import Spacer from 'components/misc/Spacer'
import sampleCheckLight from 'images/sample-check-light.png'
import sampleCheckDark from 'images/sample-check-dark.png'
import { Button, hooks, Icon as RoadieUIIcon } from 'roadie-ui'
import { formatRegistrationStep } from 'util/registrationFlow'
import { useLocalization, LocalizedText } from 'localization'

const SampleCheck = styled.div`
  ${(props) => overline(props.theme.colors.black)}

  text-align: left;
  margin: 2.4rem 0 0.8rem;
`

const Current = styled.div`
  display: flex;
  align-items: center;
  padding: 2.4rem;
  position: relative;

  & > :first-child {
    width: 4.8rem;
    height: 4.8rem;
    margin-right: 3.2rem;
  }
`

const CurrentWrapper = styled.div.attrs((props) => ({
  borderSize: props.border ? '1px' : '0px',
}))`
  border-top: ${(props) => props.borderSize} solid ${(props) => props.theme.colors.greyLight};
`

const FormWrapper = styled.div`
  padding: 0 2.4rem 2.4rem 2.4rem;
`

const IconWrapper = styled.div`
  background-color: #e6f8f8;
  border-radius: 50%;
  padding: 0.5rem;
`

const SubIconWrapper = styled.div`
  background: ${(props) => props.theme.colors[props.background]};
  border-radius: 100%;
  height: 2.4rem;
  left: 2.5rem;
  position: relative;
  top: -2rem;
  width: 2.4rem;
  z-index: 1;
`

const PaymentMethodActionWrapper = styled.div`
  ${(props) => (props.disabled ? button(props.theme.colors.greyBase) : button(props.theme.colors.primaryBase))}

  ${(props) =>
    !props.disabled &&
    css`
      &:hover {
        color: ${(props) => props.theme.colors.primaryDarker};
      }
    `}

  margin-left: auto;
  text-transform: uppercase;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
`

const PaymentMethodAction = (props) => {
  const isMobile = useSelector((state) => state.config.mediaSize === 'mobile')

  const getIconComponent = (type) => {
    if (!isMobile) return type

    switch (type) {
      case 'Cancel':
        return <RoadieUIIcon.Close $color="primary-base" $width="2rem" />
      case 'Edit':
        return <RoadieUIIcon.Pencil $color="primary-base" $height="2.5rem" />
      case 'Add':
        return <RoadieUIIcon.Plus $color="primary-base" $width="2.5rem" />
      default:
        return null
    }
  }

  const actionType = !props.collapsed ? 'Cancel' : props.exists ? 'Edit' : 'Add'
  const onClick = !props.isLocked ? props.onClick : null

  return (
    <PaymentMethodActionWrapper onClick={onClick} disabled={props.isLocked}>
      {getIconComponent(actionType)}
    </PaymentMethodActionWrapper>
  )
}

const PaymentMethodRow = (props) => {
  const content = (
    <Current id={props.id}>
      {props.icon}
      <div>
        <H6 align="left">{props.title}</H6>
        <Caption align="left" emphasis="medium">
          {props.description}
        </Caption>
      </div>
      <PaymentMethodAction
        onClick={props.collapsed ? undefined : props.onToggle}
        collapsed={props.collapsed}
        exists={props.exists}
        isLocked={props.isLocked}
      />
    </Current>
  )

  if (props.collapsed && !props.isLocked) {
    return (
      <Button.Tertiary
        type="button"
        onClick={props.onToggle}
        $display="block"
        $width="100%"
        $textAlign="inherit"
        $textTransform="none"
        $letterSpacing="0"
      >
        {content}
      </Button.Tertiary>
    )
  }

  return content
}

const BankInfoPage = (props) => {
  const { t } = useLocalization()
  const account = groupValidations(
    [minLength(6), maxLength(17)],
    t('dweb_validation_account_number_between_6_and_17_digits')
  ).required()
  const { registered, needsPage = false } = props
  const dispatch = useDispatch()
  const isWebView = useSelector((state) => !state.config.isWebview)
  const profile = useSelector((state) => state.profile)
  const isLocked = useSelector((state) => state.isLocked)
  const certs = useSelector((state) => state.trainingCertifications?.delivery_certifications_drivers)
  const theme = hooks.usePreferredColorScheme()
  const registrationStep = formatRegistrationStep('BANK_INFO', profile)

  const [stripeKey, setStripeKey] = useState(uniqueId())
  const [debitCollapsed, setDebitCollapsed] = useState(true)
  const [bankAccountCollapsed, setBankAccountCollapsed] = useState(true)

  // Check both legacy certs and new certs for the trusted driver OR the instant_pay_eligible cert
  const isTrusted =
    (profile.certifications || []).includes('trusted_driver') || !!certs?.find((cert) => cert.name === 'trusted_driver')
  const hasInstantPayCert =
    profile.certifications.includes('instant_pay_eligible') ||
    !!certs?.find((cert) => cert.name === 'instant_pay_eligible')
  const instantPayEligible = isTrusted || hasInstantPayCert

  useEffect(() => {
    fetchDriverCerts()(dispatch)
  }, [dispatch])

  const handleData = (data) => {
    if (profile.preferred_bank_account && profile.preferred_bank_account.id) {
      return dispatch(updateBankAccount(profile.preferred_bank_account.id, data)).then(() => {
        dispatch({
          type: 'PUSH_FLASH',
          flash: {
            type: 'success',
            messageKey: 'dweb_flash_profile_changes_saved',
          },
        })
        setBankAccountCollapsed(true)
      })
    } else {
      return dispatch(addBankAccount(data)).then((resp) => {
        if (registered) {
          dispatch({
            type: 'PUSH_FLASH',
            flash: {
              type: 'success',
              messageKey: 'dweb_flash_profile_changes_saved',
            },
          })
        }
        setBankAccountCollapsed(true)
        return resp
      })
    }
  }

  const handleDebit = (data) => {
    return dispatch(
      addDebitCard({
        stripe_token: data.stripe_debit_card.id,
        last4: data.stripe_debit_card.card.last4,
        expiration_month: data.stripe_debit_card.card.exp_month,
        expiration_year: data.stripe_debit_card.card.exp_year,
      })
    )
      .then(() => {
        setStripeKey(uniqueId())
        dispatch({
          type: 'PUSH_FLASH',
          flash: {
            type: 'success',
            messageKey: 'dweb_flash_profile_changes_saved',
          },
        })
        setDebitCollapsed(true)
      })
      .catch((error) => {
        const errorMessage = error?.response?.data?.errors.payment_account
        switch (errorMessage) {
          case 'Must be a debit card. This is a prepaid card.':
            dispatch({
              type: 'PUSH_FLASH',
              flash: {
                type: 'error',
                messageKey: 'dweb_flash_must_be_debit_card_prepaid',
              },
            })
            break
          case 'User must first have a standard payout account before submitting a debit account.':
            dispatch({
              type: 'PUSH_FLASH',
              flash: {
                type: 'error',
                messageKey: 'dweb_flash_bank_account_required_for_instant_pay',
              },
            })
            break
          case 'This functionality is currently disabled':
            dispatch({
              type: 'PUSH_FLASH',
              flash: {
                type: 'error',
                messageKey: 'dweb_flash_instant_pay_accounts_disabled',
              },
            })
            break
          default:
            dispatch({
              type: 'PUSH_FLASH',
              flash: {
                type: 'error',
                messageKey: 'dweb_flash_generic_error_occurred_period',
              },
            })
        }
        console.error('Error adding debit card:', error)
      })
  }

  const formatExpirationDate = (rawMonth, rawYear) => {
    const expirationMonth = rawMonth.toString().padStart(2, 0)
    const expirationYear = rawYear.toString().slice(-2)
    return `${expirationMonth}/${expirationYear}`
  }

  const banking = profile.preferred_bank_account || {}

  const instantPay = profile.preferred_instant_pay_account

  const Wrapper = !registered || needsPage ? Thin : styled.div``

  const debitCardDescription = instantPay
    ? t('dweb_account_bank_info_card_ending_exp_on', {
        lastFour: instantPay.account_number.replaceAll('X', ''),
        expirationDate: formatExpirationDate(instantPay.expiration_month, instantPay.expiration_year),
      })
    : t('dweb_account_bank_info_add_debit_card_set_up_instant_pay')

  const bankAccountDescription = banking.account_number
    ? t('dweb_account_bank_info_account_number_ending_in', {
        lastFour: banking.account_number.replaceAll('X', ''),
      })
    : t('dweb_account_bank_info_account_number_required_for_payment')

  const renderOldBankInfo = () => {
    return (
      <Form id="bank-info-form" onData={handleData}>
        <Panel>
          <PanelHeading>
            <LocalizedText translationKey="dweb_account_bank_info_title" />
          </PanelHeading>
          <PanelSubheading>
            <LocalizedText translationKey="dweb_account_bank_info_payment_issued_weekly_via_direct_deposit" />
          </PanelSubheading>

          <Input.Integer
            className="fs-block"
            label={banking.routing_number || t('dweb_account_bank_info_routing_number')}
            activeLabel={t('dweb_account_bank_info_routing_number')}
            kind="routing"
            name="routing_number"
            maxLength={9}
            validate={exactLength(9).withMessage(t('dweb_validation_routing_number_should_be_9_digits')).required()}
          />
          <Input.Integer
            className="fs-block"
            label={banking.account_number || t('dweb_account_bank_info_account_number')}
            activeLabel={t('dweb_account_bank_info_account_number')}
            name="account_number"
            maxLength={17}
            validate={account}
          />

          <SampleCheck>
            <LocalizedText translationKey="dweb_account_bank_info_sample_check" />
          </SampleCheck>

          <img
            src={theme === 'dark' ? sampleCheckDark : sampleCheckLight}
            alt={t('dweb_account_bank_info_sample_check')}
          />

          <Input.Submit
            value={!registered ? t('dweb_common_next') : needsPage ? t('dweb_common_save') : t('dweb_common_update')}
          />

          {!registered && registrationStep && <Step>{registrationStep}</Step>}
        </Panel>
      </Form>
    )
  }

  return (
    <Page sidebar={!registered ? null : <DriveSidebar />}>
      <Helmet>
        <title>{t('dweb_layout_drive_sidebar_payment_info')}</title>
      </Helmet>
      <Wrapper>
        {isWebView && profile.has_bank_account && (
          <PanelTitle>
            <LocalizedText translationKey="dweb_account_bank_info_payment_methods" />
          </PanelTitle>
        )}
        <Panel left noPadding>
          {banking.account_number && isTrusted ? (
            <PaymentMethodRow
              id="current-debit-card"
              icon={
                <IconWrapper>
                  <RoadieUIIcon.CreditCard $color="primary-dark" />
                  {!instantPay && (
                    <SubIconWrapper background={'primaryBase'}>
                      <Icon symbol="payment-cc-helper" palette="white" />
                    </SubIconWrapper>
                  )}
                </IconWrapper>
              }
              title={<LocalizedText translationKey="dweb_account_bank_info_debit_card" />}
              description={debitCardDescription}
              collapsed={debitCollapsed}
              exists={instantPay}
              isLocked={isLocked}
              onToggle={() => setDebitCollapsed(!debitCollapsed)}
            />
          ) : null}

          {!debitCollapsed && !needsPage && instantPayEligible && (
            <FormWrapper>
              <Form key={stripeKey} id="debit-card-form" onData={handleDebit}>
                <Card id="debit-card-input" name="stripe_debit_card" funding="debit" />
                <Spacer height="1.6rem" />
                <Body2>
                  <LocalizedText translationKey="dweb_account_bank_info_security_purposes_instant_pay_will_unavailable_24_hours" />
                </Body2>
                <Input.Submit
                  value={instantPay ? t('dweb_common_update') : t('dweb_common_save')}
                  disabled={isLocked}
                />
              </Form>
            </FormWrapper>
          )}

          {profile.has_bank_account ? (
            <>
              <CurrentWrapper border={!!banking.account_number && isTrusted}>
                <PaymentMethodRow
                  id="current-bank-account"
                  icon={
                    <IconWrapper>
                      <RoadieUIIcon.Bank $color="primary-dark" />
                      {!banking.account_number && (
                        <SubIconWrapper>
                          <RoadieUIIcon.AlertFilled $color="error-base" />
                        </SubIconWrapper>
                      )}
                    </IconWrapper>
                  }
                  title={<LocalizedText translationKey="dweb_account_bank_info_bank_account" />}
                  description={bankAccountDescription}
                  collapsed={bankAccountCollapsed}
                  exists={banking.account_number}
                  isLocked={isLocked}
                  onToggle={() => setBankAccountCollapsed(!bankAccountCollapsed)}
                />
              </CurrentWrapper>
              {!bankAccountCollapsed && (
                <FormWrapper>
                  <Form id="bank-info-form" onData={handleData}>
                    <Input.Integer
                      className="fs-block"
                      label={t('dweb_account_bank_info_routing_number')}
                      activeLabel={t('dweb_account_bank_info_routing_number')}
                      kind="routing"
                      name="routing_number"
                      maxLength={9}
                      validate={exactLength(9)
                        .withMessage(t('dweb_validation_routing_number_should_be_9_digits'))
                        .required()}
                    />
                    <Input.Integer
                      className="fs-block"
                      label={t('dweb_account_bank_info_account_number')}
                      activeLabel={t('dweb_account_bank_info_account_number')}
                      name="account_number"
                      maxLength={17}
                      validate={account}
                    />

                    <SampleCheck>
                      <LocalizedText translationKey="dweb_account_bank_info_sample_check" />
                    </SampleCheck>

                    <img
                      src={theme === 'dark' ? sampleCheckDark : sampleCheckLight}
                      alt={t('dweb_account_bank_info_sample_check')}
                    />

                    <Spacer height="1.6rem" />

                    <Body2>
                      <LocalizedText translationKey="dweb_account_bank_info_payment_issued_weekly_via_direct_deposit" />
                    </Body2>

                    <Input.Submit
                      value={
                        !registered
                          ? t('dweb_common_next')
                          : needsPage
                            ? t('dweb_common_save')
                            : t('dweb_common_update')
                      }
                    />

                    {!registered && registrationStep && <Step>{registrationStep}</Step>}
                  </Form>
                </FormWrapper>
              )}
            </>
          ) : (
            renderOldBankInfo()
          )}
        </Panel>
      </Wrapper>
    </Page>
  )
}

export default BankInfoPage
